import { Button , Label , Textarea } from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { PalettePreview } from "@/components/color-scheme-editor";
import { FontPreview } from "@/components/font-scheme-editor";
import { Sparkles } from "@/components/icons";
import { EditDialog } from "@/components/setting-row";
import { generateTheme, updateSiteConfig } from "@/lib/api";
import type { Palette } from "@/lib/color-scheme";
import { toAppError } from "@/lib/errors";
import { CUSTOM_TEMPLATE_ID } from "@/lib/templates";
import { randomThemeWord } from "@/lib/theme-words";
import type { FontCatalogEntry, FontScheme, GenerateThemeResp } from "@/lib/types";

interface CustomizeWithAiButtonProps {
  siteId: string;
}

/** Builds a preview-only catalog entry from a stored font selection. */
function toPreviewEntry(selection: FontScheme["heading"]): FontCatalogEntry {
  return { family: selection.family, category: selection.category, variants: [] };
}

/**
 * Page-header action that generates a full theme — color palette + font
 * pairing — from a single prompt and applies both in one save. Reuses the
 * palette and font preview components from the per-section editors; the
 * combined generation runs server-side in `generateTheme`.
 */
export function CustomizeWithAiButton({ siteId }: CustomizeWithAiButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["site", siteId] }),
      queryClient.invalidateQueries({ queryKey: ["sites"] }),
    ]);
  }, [queryClient, siteId]);

  const [open, setOpen] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [palette, setPalette] = useState<Palette | null>(null);
  const [fontScheme, setFontScheme] = useState<FontScheme | null>(null);
  const [seed, setSeed] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const openDialog = () => {
    setAiMessage("");
    setPalette(null);
    setFontScheme(null);
    setSeed(null);
    setWarnings([]);
    setOpen(true);
  };

  const aiMutation = useMutation<
    GenerateThemeResp,
    unknown,
    { message?: string; seed?: string }
  >({
    mutationFn: (input) => generateTheme(input),
    onSuccess: (resp) => {
      setPalette(resp.palette);
      setFontScheme(resp.fontScheme);
      setSeed(resp.seed);
      const next = resp.warnings ?? [];
      setWarnings(next);
      if (next.length > 0) {
        toast.warning(t("customizeAi.themeWarn"));
      } else {
        toast.success(t("customizeAi.themeSuccess"));
      }
    },
    onError: (err) => {
      setWarnings([]);
      toast.error(toAppError(err).message);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!palette || !fontScheme || !seed) {
        throw new Error(t("customizeAi.generateFirst"));
      }
      return updateSiteConfig({
        siteId,
        configPatch: {
          colorScheme: { seeds: { seed }, palette },
          fontScheme,
          templateId: CUSTOM_TEMPLATE_ID,
        },
      });
    },
    onSuccess: async () => {
      toast.success(t("customizeAi.applied"));
      await invalidate();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const generateWithAi = () => {
    const message = aiMessage.trim();
    if (!message) {
      toast.error(t("customizeAi.describeFirst"));
      return;
    }
    aiMutation.mutate({ message });
  };

  const feelingLucky = () => {
    const word = randomThemeWord();
    setAiMessage(word);
    aiMutation.mutate({ message: word });
  };

  const ready = palette !== null && fontScheme !== null;

  return (
    <>
      <Button type="button" onClick={openDialog}>
        <Sparkles /> {t("customizeAi.button")}
      </Button>
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("customizeAi.button")}
        description={t("customizeAi.description")}
        onSave={() => saveMutation.mutate()}
        saving={saveMutation.isPending}
        saveDisabled={!ready}
        saveLabel={t("customizeAi.applyTheme")}
        contentClassName="sm:max-w-2xl"
      >
        <div className="space-y-2">
          <Label htmlFor="theme-ai-message">{t("aiTools.describeVibe")}</Label>
          <Textarea
            id="theme-ai-message"
            rows={3}
            placeholder={t("colorEditor.aiPlaceholder")}
            value={aiMessage}
            onChange={(e) => setAiMessage(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t("customizeAi.promptHint")}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            className="sm:flex-1"
            onClick={feelingLucky}
            disabled={aiMutation.isPending}
          >
            {t("aiTools.feelingLucky")}
          </Button>
          <Button
            type="button"
            className="sm:flex-1"
            onClick={generateWithAi}
            disabled={aiMutation.isPending || !aiMessage.trim()}
          >
            {aiMutation.isPending ? t("aiTools.generating") : t("aiTools.generateWithAi")}
          </Button>
        </div>

        {palette && fontScheme ? (
          <div className="space-y-4">
            <FontPreview
              heading={toPreviewEntry(fontScheme.heading)}
              body={toPreviewEntry(fontScheme.body)}
              cssUrl={fontScheme.cssUrl}
              loading={aiMutation.isPending}
            />
            <PalettePreview palette={palette} loading={aiMutation.isPending} />
            {warnings.length > 0 && (
              <div className="space-y-1 rounded-md bg-muted px-3 py-2">
                <p className="text-xs font-medium text-foreground">
                  {t("colorEditor.contrastHeading")}
                </p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                  {warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
            {t("customizeAi.emptyPreview")}
          </p>
        )}
      </EditDialog>
    </>
  );
}
